import { JSX } from "react";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";

import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";

import ClearIcon from "@mui/icons-material/Clear";

import AttachmentButton from "lib/components/button/AttachmentButton";

import EditableText from "utils/edit/EditableText";
import useFetch from "utils/fetch/useFetch";

import ResourceClient from "resource/ResourceClient";

import Typography from "@mui/material/Typography";
import Expandable from "lib/components/expand/Expandable";
import { asTitle } from "utils/Utils";
import { SelectOption } from "../FieldService";
import { SelectFormAttachment } from "./SelectFormAttachment";

export type SelectFormOptionsProps = {
	index: number;
	option: SelectOption;
	editable: boolean;

	onChangeOption: (index: number, value: string) => void;
	onRemoveOption: (index: number) => void;
	onAddAttachment: (index: number, id: number, name: string) => void;
	onRemoveAttachment: (index: number, attachmentIndex: number) => void;
};

export default function SelectFormOption(
	props: Readonly<SelectFormOptionsProps>
): JSX.Element {
	const { t } = useTranslation();

	const { call } = useFetch();

	const changeOption = useCallback(
		(value: string): void => {
			props.onChangeOption(props.index, value);
		},
		[props.index, props.onChangeOption]
	);

	const removeOption = useCallback((): void => {
		props.onRemoveOption(props.index);
	}, [props.index, props.onRemoveOption]);

	const addAttachment = useCallback(
		async (file: File): Promise<void> => {
			const id = await call(
				ResourceClient.create({
					name: file.name,
					mediaType: file.type,
					userRights: []
				})
			).then(location => {
				const id = location.substring(location.lastIndexOf("/") + 1);
				return Number.parseInt(id, 10);
			});

			const data = new FormData();
			data.append("file", file);
			call(ResourceClient.updateContent(id, data)).then(() => {
				props.onAddAttachment(props.index, id, file.name);
			});
		},
		[props.index, props.onAddAttachment]
	);

	const removeAttachment = useCallback(
		(attachmentIndex: number): void => {
			props.onRemoveAttachment(props.index, attachmentIndex);
		},
		[props.index, props.onRemoveAttachment]
	);

	return (
		<ListItem>
			<Grid container>
				<Grid item container alignItems="center">
					<Grid item xs={9} sm={7} md={5} lg={4} xl={3}>
						<ListItemText>
							<EditableText
								value={props.option.value}
								editable={props.editable}
								onChange={changeOption}
							/>
						</ListItemText>
					</Grid>
					<Grid item xs={3}>
						<IconButton onClick={removeOption}>
							<ClearIcon />
						</IconButton>
						<AttachmentButton onAttach={addAttachment} />
					</Grid>
				</Grid>
				{props.option.attachments.length > 0 && (
					<Expandable
						title={
							<Typography variant="subtitle2">
								{asTitle(t("attachment_other"))}
							</Typography>
						}>
						{props.option.attachments.map(
							(attachment, attachmentIndex) => (
								<SelectFormAttachment
									key={`${attachmentIndex}-${attachment}`}
									index={attachmentIndex}
									id={attachment}
									onRemoveAttachment={removeAttachment}
								/>
							)
						)}
					</Expandable>
				)}
			</Grid>
		</ListItem>
	);
}
